/**
 * Created by shaharshelly on 02/03/2016.
 */
import IService from '../IService';
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithCustomToken,
  browserSessionPersistence,
  setPersistence,
} from 'firebase/auth';
import {
  enableLogging,
  getDatabase,
  ref,
  onValue,
  off,
} from 'firebase/database';
import app from 'app';
import _ from 'lodash-extended';

const objectTrackingPath = (trackingId) =>
  `/objectTrackings/${trackingId}/message`;

/**
 * The listener is attached to the `message` child, so the value is normally the
 * status string. Tolerate being handed the parent node instead.
 * @param val {String|Object|null}
 * @return {String|null}
 */
const readTrackingMessage = (val) => {
  if (_.isString(val)) {
    return val;
  }
  return (_.isObject(val) && val.message) || null;
};

/**
 * @namespace app.api.realTime
 */
export default IService.extend({
  whenReady: null,
  auth: {
    userId: null,
    token: null,
    domainUrl: null,
    apiKey: null,
  },
  assetListenerCounters: {},

  initialize() {
    this.enableLogging(false);
  },

  /**
   * if you want to observe firebase communication in console - call this method manually
   * @param yes
   */
  enableLogging(yes) {
    enableLogging(yes, yes);
  },

  _fetchToken() {
    return new Promise((resolve, reject) => {
      $.ajax({
        url: app.apiUrl('/api/v2/websocket/token'),
        type: 'GET',

        error: (jqXHR, textStatus) => {
          if (textStatus !== 'abort') {
            reject(this.createError(jqXHR));
          }
        },

        dataType: 'json',
        success: (res) => {
          const fields = ['token', 'domainUrl', 'apiKey', 'userId'];
          const ok = _.every(fields, (field) => {
            return (
              Object.getOwnPropertyDescriptor(res, field) &&
              _.isDefined(res[field])
            );
          });
          ok ? resolve(res) : reject('invalid response format');
        },
      });
    });
  },

  authenticate() {
    this.whenReady = new _.ColdPromise();
    this.whenReady.catch((res) => {
      if (_.isObject(res)) {
        try {
          res = JSON.stringify(res);
        } catch (ignore) {
          res = 'N/A';
        }
      }
      app.doctorController.report(`realTime: authentication failed '${res}'`);
    });

    const timeout = setTimeout(() => {
      this.whenReady.reject('TIMEOUT');
    }, app.TIME.minute(2));

    this._fetchToken().then((firebaseConfig) => {
      this.auth = Object.assign(this.auth, firebaseConfig);

      const fbApp = initializeApp(
        {
          databaseURL: this.auth.domainUrl,
          apiKey: this.auth.apiKey,
        },
        'main(readonly)'
      );
      const auth = getAuth(fbApp);

      setPersistence(auth, browserSessionPersistence).then(() => {
        signInWithCustomToken(auth, this.auth.token).then((UserCredential) => {
          clearTimeout(timeout);
          // console.log(
          //   `Last sign-in: '${UserCredential.user.metadata.lastSignInTime}'`
          // );

          const fbdb = getDatabase(fbApp);
          this.whenReady.resolve({
            fbdb,
            userId: this.auth.userId,
          });
        }, this.whenReady.reject);
      }, this.whenReady.reject);
    }, this.whenReady.reject);

    return this.whenReady;
  },

  setAppConfig() {
    this.whenReady.then(({ fbdb }) => {
      onValue(
        ref(fbdb, '/configuration/'),
        (data) => {
          app.config = data.val();
        },
        { onlyOnce: true }
      );
    });
  },

  /**
   * @param path {string}
   * @param callback {Function}
   * @param [errorCallback] {Function}
   * @param [options] {{onlyOnce: Boolean}}
   */
  listenOn({ path, callback, errorCallback, options }) {
    this.whenReady.then(
      ({ fbdb }) => {
        if (_.isEmpty(path) || !_.isFunction(callback)) {
          app.doctorController.report('listenOn: invalid arguments');
        } else {
          onValue(
            ref(fbdb, path),
            (data) => {
              if (data.exists()) {
                callback({ val: data.val() });
              }
            },
            errorCallback,
            options
          );
        }
      },
      () => {
        _.isFunction(errorCallback) && errorCallback();
      }
    );
  },

  cancelListen(path) {
    this.whenReady.then(({ fbdb }) => {
      off(ref(fbdb, path));
    });
  },

  /**
   * listen on asset readiness/metadata change update
   * @note: has self listen cancellation
   * @param {string} assetId
   * @param {{isNarration, timeout: *}|{isDynamicProvider}} [options]
   */
  listenOnAsset(assetId, options) {
    const rv = new _.ColdPromise();

    this.whenReady.then(({ fbdb, userId }) => {
      console.log('listenOnAsset', assetId);
      options = Object.assign(
        {
          isDynamicProvider: false,
          isNarration: false,
          timeout: app.TIME.minute(10),
        },
        options
      );

      if (this.assetListenerCounters[assetId]) {
        this.assetListenerCounters[assetId]++;
      } else {
        this.assetListenerCounters[assetId] = 1;
      }

      let path;
      if (options.isNarration) {
        path = `/users/${userId}/narrations/${assetId}`;
      } else if (options.isDynamicProvider) {
        path = `/public_assets/${assetId}`;
      } else {
        path = `/users/${userId}/assets/${assetId}`;
      }

      let wasUpdate = false;
      const resourceRef = ref(fbdb, path);
      const timeout = new app.namespace.Util.Timer(() => {
        rv.reject({ error: 'REALTIME:TIMEOUT' });
      }, options.timeout);
      timeout.start();

      onValue(
        resourceRef,
        (data) => {
          const value = data.val();
          if (_.isDefined(value)) {
            console.log(`listenOnAsset: ${value.message}`);
            if (value.message === 'UPDATED') {
              wasUpdate = true;
            } else if (value.message === 'DONE') {
              rv.resolve({ wasUpdate });
            } else if (value.message === 'FAILED' || value.message === 'FAIL') {
              rv.reject({ error: value.message });
            }
          }
        },
        (res) => {
          rv.reject({ error: 'REALTIME:LISTENER_ERROR', res });
        }
      );

      rv.finally(() => {
        timeout.stop();
        this.cancelListenOnAsset(assetId);
      });
    }, rv.reject);

    return rv;
  },

  cancelListenOnAsset(assetId) {
    this.whenReady.then(({ fbdb }) => {
      if (this.assetListenerCounters[assetId]) {
        this.assetListenerCounters[assetId]--;
        if (this.assetListenerCounters[assetId] > 1) {
          return; // other code is waiting for firebase response, don't cancel
        }
      }
      console.log('cancelListenOnAsset', assetId);
      off(ref(fbdb, `/users/${this.auth.userId}/assets/${assetId}`));
    });
  },

  listenOnSynthesized(options) {
    const rv = new $.Deferred();

    this.whenReady.then(({ fbdb, userId }) => {
      console.log('listenOnSynthesized', options.assetId);

      const resourceRef = ref(
        fbdb,
        `/users/${userId}/synthesized/${options.assetId}`
      );
      const timeout = setTimeout(() => {
        rv.reject('Request Timeout');
      }, app.TIME.minute(5));

      onValue(
        resourceRef,
        (data) => {
          const val = data.val();
          if (_.isDefined(val) && _.isDefined(val.message)) {
            clearTimeout(timeout);
            if ('COMPLETED' === val.message) {
              rv.resolve(val);
            } else if ('FAILED' === val.message) {
              rv.reject('Websocket failed message while processing');
            }
          }
        },
        rv.reject
      );
    }, rv.reject);

    return rv;
  },

  cancelListenOnSynthesized(assetId) {
    this.whenReady.then(({ fbdb, userId }) => {
      console.log('cancelListenOnSynthesized', userId, assetId);
      off(ref(fbdb, `/users/${userId}/synthesized/${assetId}`));
    });
  },

  listenOnSocialShareStatus(options) {
    const rv = new $.Deferred();

    this.whenReady.then(({ fbdb, userId }) => {
      console.log(
        'listenOnSocialShareStatus:',
        options.storyId,
        options.socialUploadId
      );

      const resourceRef = ref(
        fbdb,
        `/users/${userId}/stories/${options.storyId}/video/social/${options.socialUploadId}`
      );
      const timeOut = setTimeout(() => {
        rv.resolve({});
      }, app.TIME.minute(5));

      onValue(
        resourceRef,
        (data) => {
          const val = data.val();
          if (_.isDefined(val) && !_.isDefined(val.message)) {
            clearTimeout(timeOut);
            console.log('listenOnSocialShareStatus:', val.message);

            if ('finished' === val.message) {
              rv.resolve(val.message);
            } else if ('failed' === val.message) {
              rv.reject();
            } else {
              rv.notify(val.message);
            }
          }
        },
        rv.reject
      );
    }, rv.reject);

    return rv;
  },

  cancelListenOnSocialShareStatus({ storyId, socialUploadId }) {
    this.whenReady.then(({ fbdb, userId }) => {
      console.log('cancelListenOnSocialShareStatus');
      off(
        ref(
          fbdb,
          `/users/${userId}/stories/${storyId}/video/social/${socialUploadId}`
        )
      );
    });
  },

  listenRealtimePreview({ assetUUID, previewId }) {
    const rv = new $.Deferred();

    this.whenReady.then(({ fbdb, userId }) => {
      console.log('listenRealtimePreview', assetUUID, previewId);

      const resourceRef = ref(
        fbdb,
        `/users/${userId}/assets/${assetUUID}/previews/${previewId}`
      );
      const timeout = setTimeout(() => {
        rv.reject({ statusText: 'TIMEOUT' });
      }, app.TIME.minute(5));

      onValue(
        resourceRef,
        (data) => {
          const val = data.val();

          if (_.isDefined(val)) {
            clearTimeout(timeout);

            switch (val.message) {
              case 'DONE':
                rv.resolve(val);
                break;
              case 'PREVIEW_SERVICE_FAILED':
                rv.reject();
                break;
              default:
                rv.notify(val);
            }
          }
        },
        rv.reject
      );
    }, rv.reject);

    return rv;
  },

  listenRealtimePreviewParts({ assetUUID, previewId }) {
    const rv = new $.Deferred();

    this.whenReady.then(({ fbdb, userId }) => {
      const timeout = new app.namespace.Util.Timer(() => {
        rv.resolve({});
      }, app.TIME.minute(5));
      timeout.start();
      const path = `/users/${userId}/assets/${assetUUID}/preview_parts/${previewId}/parts`;

      if (serverProfile.environment !== 'prod') {
        console.log(
          'listenRealtimePreviewParts',
          `https://wochit-test.firebaseio.com${path}`
        );
      }

      onValue(
        ref(fbdb, path),
        (data) => {
          const val = data.val();
          const goodResponse = !_.some(val, (part) => {
            return 'FAILED' === part.message;
          });
          if (_.isDefined(val) && goodResponse) {
            timeout.stop();
            rv.notify(val);
          }
        },
        rv.reject
      );
    }, rv.reject);

    return rv.promise();
  },

  cancelListenRealtimePreview({ assetId, previewId }) {
    this.whenReady.then(({ fbdb, userId }) => {
      console.log('cancelListenRealtimePreview');

      off(
        ref(fbdb, `/users/${userId}/assets/${assetId}/previews/${previewId}`)
      );
      off(
        ref(
          fbdb,
          `/users/${userId}/assets/${assetId}/preview_parts/${previewId}/parts`
        )
      );
    });
  },

  listenAssetScenes(assetUUID) {
    const rv = new _.ColdPromise();

    this.whenReady.then(({ fbdb, userId }) => {
      console.log('listenAssetScenes');
      const path = `/users/${userId}/assets/${assetUUID}/videoScenes`;
      const resourceRef = ref(fbdb, path);
      const timeout = setTimeout(() => {
        rv.reject();
      }, app.TIME.minute(5));

      onValue(
        resourceRef,
        (data) => {
          const val = data.val();
          if (_.isDefined(val) && _.isDefined(val.message)) {
            if ('DONE' === val.message) {
              rv.resolve();
            } else if ('FAILED' === val.message) {
              rv.reject();
            }
          }
        },
        rv.reject
      );

      rv.finally(() => {
        clearTimeout(timeout);
        off(resourceRef);
      });
    }, rv.reject);

    return rv;
  },

  /**
   * listen on an object tracking job
   * @note: has self listen cancellation
   *
   * Unlike the other listeners this node is NOT under /users/{userId} - object
   * tracking writes to a global path, and the status is the value of `message`:
   *   /objectTrackings/{trackingId}/message -> 'DONE' | 'FAILED'
   * Anything else is treated as still running.
   *
   * @param trackingId {String}
   * @param [timeoutMin] {Number}
   * @return {_.ColdPromise}
   */
  listenOnObjectTracking({ trackingId, timeoutMin = 10 }) {
    const rv = new _.ColdPromise();

    this.whenReady.then(({ fbdb }) => {
      const path = objectTrackingPath(trackingId);
      console.log('listenOnObjectTracking', path);

      const resourceRef = ref(fbdb, path);
      const timeout = new app.namespace.Util.Timer(() => {
        rv.reject({ error: 'REALTIME:TIMEOUT' });
      }, app.TIME.minute(timeoutMin));
      timeout.start();

      onValue(
        resourceRef,
        (data) => {
          const message = readTrackingMessage(data.val());

          if (!message) {
            return; // node not written yet
          }

          console.log(`listenOnObjectTracking: ${trackingId} ${message}`);

          if ('DONE' === message) {
            rv.resolve(message);
          } else if ('FAILED' === message) {
            rv.reject({ error: message });
          }
        },
        (res) => {
          rv.reject({ error: 'REALTIME:LISTENER_ERROR', res });
        }
      );

      rv.finally(() => {
        timeout.stop();
        off(resourceRef);
      });
    }, rv.reject);

    return rv;
  },

  cancelListenOnObjectTracking(trackingId) {
    this.whenReady.then(({ fbdb }) => {
      console.log('cancelListenOnObjectTracking', trackingId);
      off(ref(fbdb, objectTrackingPath(trackingId)));
    });
  },

  listenOnTranscription({ assetUUID, timeoutMin = 5 }) {
    return new Promise((resolve, reject) => {
      this.whenReady.then(({ fbdb }) => {
        const timer = setTimeout(() => {
          console.log(`[timeout] listenOnTranscription '${assetUUID}'`);
          reject();
        }, app.TIME.minute(timeoutMin));

        console.log(
          `[now] listenOnTranscription '${assetUUID}', timeout: '${timeoutMin}'`
        );

        onValue(
          ref(fbdb, `/transcriptions/${assetUUID}`),
          (data) => {
            const val = data.val();
            if (_.isDefined(val)) {
              clearTimeout(timer);
              console.log(
                `[${val.message}] listenOnTranscription '${assetUUID}'`
              );

              if ('DONE' === val.message) {
                resolve(data);
              } else if ('FAILED' === val.message) {
                reject();
              }
            }
          },
          reject
        );
      }, reject);
    });
  },

  cancelListenOnTranscription(assetUUID) {
    this.whenReady.then(({ fbdb }) => {
      console.log(`[off] cancelListenOnTranscription '${assetUUID}`);
      off(ref(fbdb, `/transcriptions/${assetUUID}`));
    });
  },
});
